import React, { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Download, FileSpreadsheet, FileText, KeyRound, Filter, X, UserPlus, Copy, Check, Ticket, Users } from "lucide-react";
import CouponsTab from "@/pages/admin/CouponsTab";
import CustomersTab from "@/pages/admin/CustomersTab";

const statusColor = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  approved: "bg-primary/10 text-primary border-primary/30",
  rejected: "bg-red-100 text-red-800 border-red-200",
  confirmed: "bg-primary/10 text-primary border-primary/30",
  completed: "bg-blue-100 text-blue-800 border-blue-200",
  cancelled: "bg-red-100 text-red-800 border-red-200",
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [partners, setPartners] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter state (shared across bookings + partners + exports)
  const [filters, setFilters] = useState({
    vehicle_type: "", fuel_type: "", status: "",
    city: "", state: "",
    from_date: "", to_date: "",
  });
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const buildQuery = (allowed) => {
    const p = new URLSearchParams();
    for (const k of allowed) {
      const v = filters[k];
      if (v) p.append(k, v);
    }
    const s = p.toString();
    return s ? `?${s}` : "";
  };

  const partnerAllowed = ["status", "city", "state", "vehicle_type"];
  const bookingAllowed = ["vehicle_type", "fuel_type", "status", "city", "state", "from_date", "to_date"];

  const load = async () => {
    setLoading(true);
    try {
      const [s, p, b] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/partners" + buildQuery(partnerAllowed)),
        api.get("/admin/bookings" + buildQuery(bookingAllowed)),
      ]);
      setStats(s.data);
      setPartners(p.data);
      setBookings(b.data);
    } catch {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [
    filters.vehicle_type, filters.fuel_type, filters.status,
    filters.city, filters.state, filters.from_date, filters.to_date,
  ]);

  const clearFilters = () => setFilters({ vehicle_type: "", fuel_type: "", status: "", city: "", state: "", from_date: "", to_date: "" });

  const updatePartner = async (pid, status) => {
    try {
      await api.put(`/admin/partners/${pid}`, { status });
      toast.success(`Partner ${status}`);
      load();
    } catch {
      toast.error("Failed");
    }
  };

  const [pwOpen, setPwOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwSubmitting, setPwSubmitting] = useState(false);

  // Manual partner creation
  const emptyPartnerForm = {
    store_name: "", address: "", city: "", pincode: "", state: "",
    contact_name: "", contact_phone: "", email: "",
    licence_number: "", licence_authority: "", licence_expiry: "",
    services_offered: ["two_wheeler", "four_wheeler"],
    latitude: "", longitude: "", auto_approve: true,
  };
  const [addPartnerOpen, setAddPartnerOpen] = useState(false);
  const [partnerForm, setPartnerForm] = useState(emptyPartnerForm);
  const [addPartnerSubmitting, setAddPartnerSubmitting] = useState(false);
  const [createdCreds, setCreatedCreds] = useState(null);
  const [copied, setCopied] = useState(false);

  const toggleService = (svc) => {
    setPartnerForm((f) => {
      const s = new Set(f.services_offered);
      if (s.has(svc)) s.delete(svc); else s.add(svc);
      return { ...f, services_offered: Array.from(s) };
    });
  };

  const submitManualPartner = async (e) => {
    e.preventDefault();
    if (partnerForm.services_offered.length === 0) { toast.error("Select at least one vehicle service"); return; }
    setAddPartnerSubmitting(true);
    try {
      const payload = {
        ...partnerForm,
        latitude: partnerForm.latitude ? Number(partnerForm.latitude) : null,
        longitude: partnerForm.longitude ? Number(partnerForm.longitude) : null,
      };
      const res = await api.post("/admin/partners/manual", payload);
      setCreatedCreds({
        login_email: res.data.credentials.login_email,
        temp_password: res.data.credentials.temp_password,
        store_name: res.data.partner.store_name,
        email_sent: res.data.notifications.email_sent,
        sms_sent: res.data.notifications.sms_sent,
      });
      setPartnerForm(emptyPartnerForm);
      toast.success("Partner account created");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create partner");
    } finally {
      setAddPartnerSubmitting(false);
    }
  };

  const copyCreds = async () => {
    if (!createdCreds) return;
    const text = `VayuCheck partner login\nStore: ${createdCreds.store_name}\nEmail: ${createdCreds.login_email}\nTemporary password: ${createdCreds.temp_password}\n\nSign in at: ${window.location.origin}/login`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Clipboard blocked — please copy manually");
    }
  };

  const submitPasswordChange = async (e) => {
    e.preventDefault();
    if (pwForm.new_password.length < 6) { toast.error("New password must be 6+ characters"); return; }
    if (pwForm.new_password !== pwForm.confirm) { toast.error("Passwords do not match"); return; }
    setPwSubmitting(true);
    try {
      await api.post("/auth/change-password", { current_password: pwForm.current_password, new_password: pwForm.new_password });
      toast.success("Password changed. Use the new password next time you sign in.");
      setPwOpen(false);
      setPwForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setPwSubmitting(false);
    }
  };

  const downloadExport = async (path, filename) => {
    try {
      const token = localStorage.getItem("vc_token");
      const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch {
      toast.error("Download failed");
    }
  };

  if (loading) return <div className="min-h-screen bg-background text-foreground"><Navbar /><div className="p-8 text-muted-foreground">Loading…</div></div>;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <div className="mx-auto max-w-7xl px-6 py-12">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-primary mb-2">Admin console</div>
            <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">Platform overview</h1>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Dialog open={addPartnerOpen} onOpenChange={(o) => { setAddPartnerOpen(o); if (!o) setCreatedCreds(null); }}>
              <DialogTrigger asChild>
                <Button data-testid="add-partner-btn" className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 h-11">
                  <UserPlus className="w-4 h-4 mr-2" /> Add partner manually
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="add-partner-dialog" className="rounded-2xl border-border bg-card max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-display tracking-tight">Create a partner account</DialogTitle>
                </DialogHeader>

                {createdCreds ? (
                  <div className="space-y-4">
                    <div className="border border-primary/30 bg-primary/5 rounded-xl p-5">
                      <div className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-2">Account created · Share these credentials</div>
                      <div className="text-sm text-muted-foreground mb-4">
                        Email {createdCreds.email_sent ? <span className="text-green-700 font-medium">sent ✓</span> : <span className="text-amber-700 font-medium">not sent (Resend key missing)</span>}
                        {" · "}
                        SMS {createdCreds.sms_sent ? <span className="text-green-700 font-medium">sent ✓</span> : <span className="text-amber-700 font-medium">not sent (Twilio key missing)</span>}
                      </div>
                      <div className="space-y-2 font-mono text-sm">
                        <div><span className="text-muted-foreground">Store:</span> <b>{createdCreds.store_name}</b></div>
                        <div><span className="text-muted-foreground">Login email:</span> <b data-testid="cred-email">{createdCreds.login_email}</b></div>
                        <div><span className="text-muted-foreground">Temp password:</span> <b data-testid="cred-password" className="text-primary text-base">{createdCreds.temp_password}</b></div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-4">
                        🔒 The partner will be required to change this password on their first sign-in.
                      </p>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button data-testid="copy-creds-btn" onClick={copyCreds} variant="outline" className="rounded-lg border-border">
                        {copied ? <><Check className="w-4 h-4 mr-2" /> Copied</> : <><Copy className="w-4 h-4 mr-2" /> Copy for WhatsApp / SMS</>}
                      </Button>
                      <Button data-testid="add-another-btn" onClick={() => setCreatedCreds(null)} className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90">
                        Add another
                      </Button>
                    </div>
                  </div>
                ) : (
                <form onSubmit={submitManualPartner} className="space-y-4">
                  <div className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">Owner / Contact</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Contact name *</Label>
                      <Input data-testid="mp-name" required value={partnerForm.contact_name} onChange={(e) => setPartnerForm({ ...partnerForm, contact_name: e.target.value })} className="rounded-lg bg-secondary border-border h-10 mt-1" />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Login email *</Label>
                      <Input data-testid="mp-email" type="email" required value={partnerForm.email} onChange={(e) => setPartnerForm({ ...partnerForm, email: e.target.value })} className="rounded-lg bg-secondary border-border h-10 mt-1" placeholder="partner@centre.in" />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Contact phone *</Label>
                      <Input data-testid="mp-phone" required value={partnerForm.contact_phone} onChange={(e) => setPartnerForm({ ...partnerForm, contact_phone: e.target.value })} className="rounded-lg bg-secondary border-border h-10 mt-1" placeholder="10-digit or +91..." />
                    </div>
                  </div>

                  <div className="text-xs uppercase tracking-[0.2em] text-primary font-semibold pt-2">Store</div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Store name *</Label>
                    <Input data-testid="mp-store" required value={partnerForm.store_name} onChange={(e) => setPartnerForm({ ...partnerForm, store_name: e.target.value })} className="rounded-lg bg-secondary border-border h-10 mt-1" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Address *</Label>
                    <Input data-testid="mp-address" required value={partnerForm.address} onChange={(e) => setPartnerForm({ ...partnerForm, address: e.target.value })} className="rounded-lg bg-secondary border-border h-10 mt-1" />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">City *</Label>
                      <Input data-testid="mp-city" required value={partnerForm.city} onChange={(e) => setPartnerForm({ ...partnerForm, city: e.target.value })} className="rounded-lg bg-secondary border-border h-10 mt-1" />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">State *</Label>
                      <Input data-testid="mp-state" required value={partnerForm.state} onChange={(e) => setPartnerForm({ ...partnerForm, state: e.target.value })} className="rounded-lg bg-secondary border-border h-10 mt-1" />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Pincode *</Label>
                      <Input data-testid="mp-pin" required value={partnerForm.pincode} onChange={(e) => setPartnerForm({ ...partnerForm, pincode: e.target.value })} className="rounded-lg bg-secondary border-border h-10 mt-1" />
                    </div>
                  </div>

                  <div className="text-xs uppercase tracking-[0.2em] text-primary font-semibold pt-2">Licence</div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Licence number *</Label>
                      <Input data-testid="mp-lic-num" required value={partnerForm.licence_number} onChange={(e) => setPartnerForm({ ...partnerForm, licence_number: e.target.value })} className="rounded-lg bg-secondary border-border h-10 mt-1" />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Authority *</Label>
                      <Input data-testid="mp-lic-auth" required value={partnerForm.licence_authority} onChange={(e) => setPartnerForm({ ...partnerForm, licence_authority: e.target.value })} className="rounded-lg bg-secondary border-border h-10 mt-1" placeholder="RTO / State" />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Expiry *</Label>
                      <Input data-testid="mp-lic-expiry" type="date" required value={partnerForm.licence_expiry} onChange={(e) => setPartnerForm({ ...partnerForm, licence_expiry: e.target.value })} className="rounded-lg bg-secondary border-border h-10 mt-1" />
                    </div>
                  </div>

                  <div className="text-xs uppercase tracking-[0.2em] text-primary font-semibold pt-2">Services offered</div>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { v: "two_wheeler", label: "Two-wheeler" },
                      { v: "three_wheeler", label: "Three-wheeler" },
                      { v: "four_wheeler", label: "Four-wheeler" },
                      { v: "commercial", label: "Commercial" },
                    ].map((s) => {
                      const active = partnerForm.services_offered.includes(s.v);
                      return (
                        <button
                          type="button"
                          key={s.v}
                          data-testid={`mp-svc-${s.v}`}
                          onClick={() => toggleService(s.v)}
                          className={`px-3 py-1.5 rounded-full text-xs border transition ${active ? "bg-primary text-primary-foreground border-primary" : "bg-secondary text-foreground border-border hover:border-primary/50"}`}
                        >
                          {s.label}
                        </button>
                      );
                    })}
                  </div>

                  <div className="flex items-center gap-2 pt-2">
                    <input
                      data-testid="mp-auto-approve"
                      id="mp-auto-approve"
                      type="checkbox"
                      checked={partnerForm.auto_approve}
                      onChange={(e) => setPartnerForm({ ...partnerForm, auto_approve: e.target.checked })}
                      className="h-4 w-4 rounded border-border"
                    />
                    <Label htmlFor="mp-auto-approve" className="text-sm text-foreground cursor-pointer">Auto-approve and make store live immediately</Label>
                  </div>

                  <DialogFooter className="pt-2">
                    <Button data-testid="mp-submit" type="submit" disabled={addPartnerSubmitting} className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
                      {addPartnerSubmitting ? "Creating…" : "Create partner & email credentials"}
                    </Button>
                  </DialogFooter>
                </form>
                )}
              </DialogContent>
            </Dialog>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="export-menu-btn" variant="outline" className="rounded-lg border-border text-foreground hover:bg-secondary h-11">
                  <Download className="w-4 h-4 mr-2" /> Export data
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-lg">
                <DropdownMenuLabel>Partners{activeFilterCount > 0 ? " (filtered)" : ""}</DropdownMenuLabel>
                <DropdownMenuItem data-testid="export-partners-xlsx" onClick={() => downloadExport(`/admin/export/partners.xlsx${buildQuery(partnerAllowed)}`, "vayucheck_partners.xlsx")}>
                  <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-600" /> Excel (.xlsx)
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="export-partners-pdf" onClick={() => downloadExport(`/admin/export/partners.pdf${buildQuery(partnerAllowed)}`, "vayucheck_partners.pdf")}>
                  <FileText className="w-4 h-4 mr-2 text-red-600" /> PDF (.pdf)
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Bookings{activeFilterCount > 0 ? " (filtered)" : ""}</DropdownMenuLabel>
                <DropdownMenuItem data-testid="export-bookings-xlsx" onClick={() => downloadExport(`/admin/export/bookings.xlsx${buildQuery(bookingAllowed)}`, "vayucheck_bookings.xlsx")}>
                  <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-600" /> Excel (.xlsx)
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="export-bookings-pdf" onClick={() => downloadExport(`/admin/export/bookings.pdf${buildQuery(bookingAllowed)}`, "vayucheck_bookings.pdf")}>
                  <FileText className="w-4 h-4 mr-2 text-red-600" /> PDF (.pdf)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Dialog open={pwOpen} onOpenChange={setPwOpen}>
              <DialogTrigger asChild>
                <Button data-testid="change-password-btn" variant="outline" className="rounded-lg border-border text-foreground hover:bg-secondary h-11">
                  <KeyRound className="w-4 h-4 mr-2" /> Change password
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="change-password-dialog" className="rounded-2xl border-border bg-card">
                <DialogHeader>
                  <DialogTitle className="font-display tracking-tight">Change your password</DialogTitle>
                </DialogHeader>
                <form onSubmit={submitPasswordChange} className="space-y-4">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Current password</Label>
                    <Input data-testid="pw-current" type="password" required value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} className="rounded-lg bg-secondary border-border h-11 mt-2" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">New password</Label>
                    <Input data-testid="pw-new" type="password" required minLength={6} value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} className="rounded-lg bg-secondary border-border h-11 mt-2" placeholder="Min 6 characters" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Confirm new password</Label>
                    <Input data-testid="pw-confirm" type="password" required minLength={6} value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} className="rounded-lg bg-secondary border-border h-11 mt-2" />
                  </div>
                  <DialogFooter>
                    <Button data-testid="pw-submit" type="submit" disabled={pwSubmitting} className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
                      {pwSubmitting ? "Saving…" : "Update password"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border mb-12" data-testid="admin-stats">
          {[
            { k: stats?.total_users, v: "Total users" },
            { k: stats?.total_partners, v: "Total partners" },
            { k: stats?.pending_partners, v: "Pending partners" },
            { k: stats?.total_bookings, v: "Total bookings" },
            { k: stats?.approved_partners, v: "Approved" },
            { k: stats?.pending_bookings, v: "Bookings pending" },
            { k: stats?.completed_bookings, v: "Bookings done" },
            { k: "24/7", v: "Availability" },
          ].map((s, i) => (
            <div key={i} className="bg-card p-6">
              <div className="font-display text-3xl font-black tracking-tight text-primary">{s.k ?? 0}</div>
              <div className="text-xs uppercase tracking-[0.15em] mt-2 text-muted-foreground">{s.v}</div>
            </div>
          ))}
        </div>

        {/* Reports Filter */}
        <div className="border border-border bg-white rounded-2xl p-5 mb-8" data-testid="reports-filter">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-primary">
              <Filter className="w-3.5 h-3.5" /> Reports & Filters {activeFilterCount > 0 && <span className="ml-1 text-foreground bg-primary/10 border border-primary/30 px-2 py-0.5 rounded-full normal-case tracking-normal text-[10px]">{activeFilterCount} active</span>}
            </div>
            {activeFilterCount > 0 && (
              <button data-testid="clear-filters" onClick={clearFilters} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
                <X className="w-3 h-3" /> Clear all
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Vehicle class</Label>
              <Select value={filters.vehicle_type || "all"} onValueChange={(v) => setFilters({ ...filters, vehicle_type: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="filter-vehicle-type" className="rounded-lg bg-secondary border-border h-10 mt-1 text-sm"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent className="rounded-lg">
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="two_wheeler">Two-wheeler</SelectItem>
                  <SelectItem value="three_wheeler">Three-wheeler</SelectItem>
                  <SelectItem value="four_wheeler">Four-wheeler</SelectItem>
                  <SelectItem value="commercial">Commercial</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Fuel type</Label>
              <Select value={filters.fuel_type || "all"} onValueChange={(v) => setFilters({ ...filters, fuel_type: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="filter-fuel-type" className="rounded-lg bg-secondary border-border h-10 mt-1 text-sm"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent className="rounded-lg">
                  <SelectItem value="all">All</SelectItem>
                  {["petrol","diesel","cng","lpg","ev","hybrid"].map((f) => <SelectItem key={f} value={f}>{f.toUpperCase()}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Status</Label>
              <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="filter-status" className="rounded-lg bg-secondary border-border h-10 mt-1 text-sm"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent className="rounded-lg">
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="pending">Pending <span className="text-muted-foreground text-xs ml-1">(both)</span></SelectItem>
                  <SelectItem value="approved">Approved <span className="text-muted-foreground text-xs ml-1">(partners)</span></SelectItem>
                  <SelectItem value="rejected">Rejected <span className="text-muted-foreground text-xs ml-1">(partners)</span></SelectItem>
                  <SelectItem value="confirmed">Confirmed <span className="text-muted-foreground text-xs ml-1">(bookings)</span></SelectItem>
                  <SelectItem value="completed">Completed <span className="text-muted-foreground text-xs ml-1">(bookings)</span></SelectItem>
                  <SelectItem value="cancelled">Cancelled <span className="text-muted-foreground text-xs ml-1">(bookings)</span></SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">City</Label>
              <Input data-testid="filter-city" value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })} placeholder="e.g. Bengaluru" className="rounded-lg bg-secondary border-border h-10 mt-1 text-sm" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">State</Label>
              <Input data-testid="filter-state" value={filters.state} onChange={(e) => setFilters({ ...filters, state: e.target.value })} placeholder="e.g. Karnataka" className="rounded-lg bg-secondary border-border h-10 mt-1 text-sm" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">From date</Label>
              <Input data-testid="filter-from-date" type="date" value={filters.from_date} onChange={(e) => setFilters({ ...filters, from_date: e.target.value })} className="rounded-lg bg-secondary border-border h-10 mt-1 text-sm" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">To date</Label>
              <Input data-testid="filter-to-date" type="date" value={filters.to_date} onChange={(e) => setFilters({ ...filters, to_date: e.target.value })} className="rounded-lg bg-secondary border-border h-10 mt-1 text-sm" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-4">
            Filters apply to both the tables below and the Export data downloads. Date range filters bookings by creation date. State/City filter partners directly and bookings via their partner centre.
          </p>
        </div>

        <Tabs defaultValue="partners">
          <TabsList className="rounded-lg bg-secondary border border-border">
            <TabsTrigger data-testid="tab-partners" value="partners" className="rounded-lg">Partners ({partners.length})</TabsTrigger>
            <TabsTrigger data-testid="tab-customers" value="customers" className="rounded-lg"><Users className="w-3.5 h-3.5 mr-1.5" /> Users</TabsTrigger>
            <TabsTrigger data-testid="tab-bookings" value="bookings" className="rounded-lg">Bookings ({bookings.length})</TabsTrigger>
            <TabsTrigger data-testid="tab-coupons" value="coupons" className="rounded-lg"><Ticket className="w-3.5 h-3.5 mr-1.5" /> Coupons</TabsTrigger>
          </TabsList>

          <TabsContent value="partners" className="mt-6">
            <div className="border border-border bg-card">
              <Table data-testid="admin-partners-table">
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead className="text-xs uppercase tracking-[0.15em]">Store</TableHead>
                    <TableHead className="text-xs uppercase tracking-[0.15em]">City</TableHead>
                    <TableHead className="text-xs uppercase tracking-[0.15em]">Licence</TableHead>
                    <TableHead className="text-xs uppercase tracking-[0.15em]">Contact</TableHead>
                    <TableHead className="text-xs uppercase tracking-[0.15em]">Status</TableHead>
                    <TableHead className="text-xs uppercase tracking-[0.15em]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {partners.map((p) => (
                    <TableRow key={p.partner_id} className="border-border">
                      <TableCell>
                        <div className="font-medium">{p.store_name}</div>
                        <div className="text-xs text-muted-foreground">{p.address}</div>
                      </TableCell>
                      <TableCell>{p.city} · {p.pincode}</TableCell>
                      <TableCell>
                        <div className="font-mono text-xs">{p.licence_number}</div>
                        <div className="text-xs text-muted-foreground">{p.licence_authority}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{p.contact_name}</div>
                        <div className="text-xs text-muted-foreground">{p.contact_phone}</div>
                      </TableCell>
                      <TableCell><Badge className={`rounded-lg border ${statusColor[p.status]}`}>{p.status}</Badge></TableCell>
                      <TableCell className="space-x-2">
                        {p.status !== "approved" && (
                          <Button data-testid={`approve-${p.partner_id}`} size="sm" onClick={() => updatePartner(p.partner_id, "approved")} className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 h-8">Approve</Button>
                        )}
                        {p.status !== "rejected" && (
                          <Button data-testid={`reject-${p.partner_id}`} size="sm" variant="outline" onClick={() => updatePartner(p.partner_id, "rejected")} className="rounded-lg border-border text-foreground hover:bg-secondary h-8">Reject</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {partners.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No partners</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="bookings" className="mt-6">
            <div className="border border-border bg-card">
              <Table data-testid="admin-bookings-table">
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead className="text-xs uppercase tracking-[0.15em]">Date</TableHead>
                    <TableHead className="text-xs uppercase tracking-[0.15em]">Customer</TableHead>
                    <TableHead className="text-xs uppercase tracking-[0.15em]">Centre</TableHead>
                    <TableHead className="text-xs uppercase tracking-[0.15em]">Vehicle</TableHead>
                    <TableHead className="text-xs uppercase tracking-[0.15em]">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bookings.map((b) => (
                    <TableRow key={b.booking_id} className="border-border">
                      <TableCell className="text-xs text-muted-foreground">{new Date(b.created_at).toLocaleDateString()}</TableCell>
                      <TableCell><div>{b.customer_name}</div><div className="text-xs text-muted-foreground">{b.customer_phone}</div></TableCell>
                      <TableCell><div>{b.partner_snapshot?.store_name}</div><div className="text-xs text-muted-foreground">{b.partner_snapshot?.city}</div></TableCell>
                      <TableCell><div className="font-mono">{b.vehicle_number}</div><div className="text-xs text-muted-foreground">{b.vehicle_type.replace("_", " ")} · {b.fuel_type}</div></TableCell>
                      <TableCell><Badge className={`rounded-lg border ${statusColor[b.status]}`}>{b.status}</Badge></TableCell>
                    </TableRow>
                  ))}
                  {bookings.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No bookings</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="coupons" className="mt-6">
            <CouponsTab />
          </TabsContent>

          <TabsContent value="customers" className="mt-6">
            <CustomersTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
